<template>
  <div class="edt">
    <v-app-bar
      flat
      color="transparent"
      class="main-appbar"
    >
      <app-bar-nav-icon />
      <div class="d-flex align-center">
        <v-toolbar-title>
          <h1 class="headline font-weight-bold">{{ pageTitle }}
          </h1>
        </v-toolbar-title>
      </div>
      <v-spacer />
      <div>
        <v-btn
          color="primary"
          class="mr-4"
          medium
          @click="onCancel"
          :disabled="form.$busy"
          text
          style="text-transform: none;"
        >Cancel</v-btn>
        <v-btn
          color="primary"
          medium
          @click="onSave"
          :loading="form.$busy"
          style="text-transform: none;"
        >Save</v-btn>
      </div>
    </v-app-bar>
    <v-row class="px-4 py-4">
      <v-col
        lg="8"
        md="6"
        sm="12"
        xs="12"
      >
        <v-card
          outlined
          class="px-6 py-8"
        >

          <image-file-input-viewer
            label="Author Thumbnail"
            v-model="form.thumbnail"
            :error-messages="replace(head(form.$getError('thumbnail')), 'The thumbnail field', 'Author thumbnail')"
          />

          <v-text-field
            v-model="form.first_name"
            outlined
            label="First name"
            :error-messages="form.$getError('first_name')"
          />

          <v-text-field
            v-model="form.last_name"
            outlined
            label="Last name"
            :error-messages="form.$getError('last_name')"
          />

          <v-text-field
            v-model="form.social_username"
            outlined
            label="Social username"
            :error-messages="form.$getError('social_username')"
          />

          <v-text-field
            v-model="form.job_title"
            outlined
            label="Job Title"
            :error-messages="form.$getError('job_title')"
          />

          <v-text-field
            v-model="form.alumni_of"
            outlined
            label="Alumni Of"
            :error-messages="form.$getError('alumni_of')"
          />

          <v-text-field
            v-model="form.award"
            outlined
            label="Award"
            :error-messages="form.$getError('award')"
          />

          <v-text-field
            v-model="form.years_of_experience"
            type="number"
            outlined
            label="Years of experience"
            :error-messages="form.$getError('years_of_experience')"
          />

          <normal-content-editor
            v-model="form.description"
            editorHeight="200"
            label="Bio"
            :errorMessages="form.$getError('description')"
            class="mb-6"
          />

          <normal-content-editor
            v-model="form.short_bio"
            editorHeight="200"
            label="Short Bio"
            :errorMessages="form.$getError('short_bio')"
            class="mb-6"
          />

        </v-card>
      </v-col>
      <v-col
        lg="4"
        md="6"
        sm="12"
      >
        <slug-generator
          ref="slugGenerator"
          :base-string="displayName"
          component-label="Author slug"
          :isPath="false"
          v-model="slug"
          :error-messages="form.$getError('slug')"
        />

        <author-socials :errorMessages="form.$errors" v-bind:facebookLink.sync="form.facebook_link" v-bind:linkedinLink.sync="form.linkedin_link"/>

        <TextFieldArrayInput
          v-bind:items.sync="form.same_as"
          title="Same As"
          class="mt-6"
          :errorMessages="form.$errors"
        />

        <!-- <v-card
          outlined
          class="px-6 mt-6"
        >
          <div class="d-flex flex-row justify-space-between">
            <label class="subtitle-1 grey--text pt-1 mt-4">Is Contributor?</label>
            <v-checkbox
              color="primary"
              v-model="form.is_contributor"
              :error-messages="form.$getError('is_contributor')"
            />
          </div>
        </v-card> -->

        <social-sharing-meta-tag
          v-bind:title.sync="form.social_summary_title"
          v-bind:description.sync="form.social_summary_description"
          v-bind:image.sync="form.social_thumbnail"
          class="mt-6"
        />

        <image-alt-text v-bind:imageAltText.sync="form.image_alt_text" />

      </v-col>
    </v-row>
    <v-overlay
      absolute
      :value="updating"
    >
      <v-dialog
        v-model="updating"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="primary"
          dark
        >
          <v-card-text>
            {{ loading_message }}
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-overlay>
  </div>
</template>

<script>
import Form from '@/utils/form'
import get from 'lodash/get'
import head from 'lodash/head'
import replace from 'lodash/replace'
import Author from '@/models/Author'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import { mapActions } from 'vuex'
import ImageFileInputViewer from '@/components/ImageFileInputViewer'
import ImageAltText from '@/components/ImageAltText'
import SocialSharingMetaTag from '@/components/SocialSharingMetaTag'
import NormalContentEditor from '@/components/NormalContentEditor'
import slugify from 'slugify'
import SlugGenerator from '@/components/SlugGenerator'
import TextFieldArrayInput from '@/components/TextFieldArrayInput'
import isEmpty from 'lodash/isEmpty'
import AuthorSocials from '@/components/AuthorSocials'

const initialData = {
  last_name: null,
  first_name: null,
  description: null,
  social_username: null,
  is_contributor: false,
  social_summary_title: null,
  social_summary_description: null,
  image_alt_text: null,
  thumbnail: null,
  social_thumbnail: null,
  slug: null,
  short_bio: null,
  job_title: null,
  alumni_of: null,
  award: null,
  same_as: [],
  years_of_experience: null,
  facebook_link: null,
  linkedin_link: null
}

export default {
  data () {
    return {
      loading: false,
      updating: false,
      loading_message: 'Loading',
      snackbar: {
        show: false,
        message: null,
        color: 'success'
      },
      form: new Form({
        ...initialData
      }),
      slugBaseString: null
    }
  },
  props: {
    author: {
      type: [Author, Object],
      default () {
        return {}
      }
    },
    pageTitle: {
      type: String,
      default: 'Author'
    },
    redirectCancelTo: {
      type: [String, Object],
      default () {
        return {
          name: 'authors'
        }
      }
    },
    redirectSaveTo: {
      type: [String, Object],
      default () {
        return {
          name: 'authors'
        }
      }
    }
  },

  components: {
    AppBarNavIcon,
    ImageFileInputViewer,
    ImageAltText,
    SocialSharingMetaTag,
    NormalContentEditor,
    SlugGenerator,
    TextFieldArrayInput,
    AuthorSocials

  },

  methods: {
    head,
    replace,
    ...mapActions({
      updateRecord: 'author/update'
    }),

    onCancel () {
      this.$router.replace(this.redirectCancelTo)
    },

    async onSave () {
      try {
        this.form.$busy = true
        this.form.$clearErrors()
        const data = this.form.$data()

        data.slug = this.slug

        if (this.isModeUpdate) {
          data._method = 'put'

          if (data.thumbnail && data.thumbnail.id) { this.$delete(data, 'thumbnail') }
          if (data.social_thumbnail && data.social_thumbnail.id) { this.$delete(data, 'social_thumbnail') }
        }

        const author = new Author(data)
        await author.save()

        this.$router.replace(this.redirectSaveTo)
      } catch (error) {
        if (get(error, 'response.status') === 422) {
          if (get(error, 'response.data.errors') !== null) {
            this.form.$setErrors(get(error, 'response.data.errors'))
          } else {
            this.showSnackbar('Something went wrong.', 'error')
          }
        } else {
          this.showSnackbar(error, 'error')
        }
      } finally {
        this.form.$busy = false
      }
    },
    showSnackbar (message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    }

  },

  watch: {
    author: {
      immediate: true,
      handler (val) {
        if (val) {
          this.form = new Form({
            ...initialData,
            ...val
          })

          if (val.thumbnail) {
            this.form.image_alt_text = val.thumbnail.custom_properties.alt_text
          }

          if (!(this.form.first_name || this.form.last_name)) {
            this.$nextTick(() => this.$refs['slugGenerator'].generate())
          }
        }
      }
    }
  },

  computed: {
    isModeUpdate () {
      return get(this.form, 'id', null) !== null
    },

    slug: {
      get () {
        if (this.form.slug && this.form.slug.trim() !== '') {
          return this.form.slug
        }

        if (this.form.first_name || this.form.last_name) {
          return slugify(this.displayName, {
            remove: /[*+~.()'"!:@]/g,
            lower: true,
            strict: true
          })
        }

        return ''
      },
      set (v) {
        this.form.slug = v
      }
    },

    displayName () {
      let firstName = this.form.first_name || ''
      let lastName = this.form.last_name || ''

      let displayName = firstName + ' ' + lastName

      return displayName
    }
  }
}
</script>
